export interface IBrandIdentity {
  brandName?: string
  supplierName?: string
  colorId?: string
}

type BrandIdentityFields = {
  brand_name?: unknown
  brandName?: unknown
  supplier_name?: unknown
  supplierName?: unknown
  color_id?: unknown
  colorId?: unknown
}

type BrandIdentityRecord = object

function normalizeValue(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined
  const normalizedValue = String(value).trim()
  return normalizedValue || undefined
}

function normalizeColorId(value: unknown): string | undefined {
  const colorId = normalizeValue(value)
  return colorId === '0' ? undefined : colorId
}

function getFirstNormalizedValue(
  values: unknown[],
  normalize: (value: unknown) => string | undefined
): string | undefined {
  for (const value of values) {
    const normalizedValue = normalize(value)
    if (normalizedValue) return normalizedValue
  }
  return undefined
}

export function getBrandIdentity(record: BrandIdentityRecord): IBrandIdentity {
  const fields = record as BrandIdentityFields
  return {
    brandName: getFirstNormalizedValue(
      [fields.brandName, fields.brand_name],
      normalizeValue
    ),
    supplierName: getFirstNormalizedValue(
      [fields.supplierName, fields.supplier_name],
      normalizeValue
    ),
    colorId: getFirstNormalizedValue(
      [fields.colorId, fields.color_id],
      normalizeColorId
    ),
  }
}

export function isBrandRecord(record: BrandIdentityRecord): boolean {
  const identity = getBrandIdentity(record)
  return Boolean(
    identity.brandName || identity.supplierName || identity.colorId
  )
}

/** Matches the material-list and layout definition of a brand board. */
export function hasCompleteBrandIdentity(record: BrandIdentityRecord): boolean {
  const identity = getBrandIdentity(record)
  return Boolean(
    identity.colorId && identity.brandName && identity.supplierName
  )
}

export function getBrandIdentityKey(record: BrandIdentityRecord): string {
  const identity = getBrandIdentity(record)
  if (!isBrandRecord(record)) return JSON.stringify(['non-brand'])

  return JSON.stringify([
    'brand',
    identity.colorId || null,
    identity.brandName || null,
    identity.supplierName || null,
  ])
}

type MaterialCategoryFields = BrandIdentityFields & {
  matCode?: unknown
  thick?: unknown
  texture?: unknown
  is_high_gloss_plank?: unknown
}

/**
 * Key used only for grouping material-list category rows.
 * Keep this separate from part.symbol/typeSymbol, which layout workflows parse.
 */
export function getMaterialCategoryKey(record: BrandIdentityRecord): string {
  const fields = record as MaterialCategoryFields
  const brandCategoryKey = hasCompleteBrandIdentity(record)
    ? getBrandIdentityKey(record)
    : JSON.stringify(['non-brand'])
  return JSON.stringify([
    fields.matCode ?? '',
    fields.thick ?? '',
    fields.texture ?? '',
    fields.is_high_gloss_plank ?? '',
    brandCategoryKey,
  ])
}

export function isBrandIdentityCompatible(
  left: BrandIdentityRecord,
  right: BrandIdentityRecord
): boolean {
  const leftIdentity = getBrandIdentity(left)
  const rightIdentity = getBrandIdentity(right)
  const leftIsBrand = isBrandRecord(left)
  const rightIsBrand = isBrandRecord(right)

  if (!leftIsBrand && !rightIsBrand) return true
  if (leftIsBrand !== rightIsBrand) return false

  const keys: Array<keyof IBrandIdentity> = [
    'brandName',
    'supplierName',
    'colorId',
  ]
  let hasMatchedIdentity = false

  for (const key of keys) {
    const leftValue = leftIdentity[key]
    const rightValue = rightIdentity[key]
    if (!leftValue || !rightValue) continue
    if (leftValue !== rightValue) return false
    hasMatchedIdentity = true
  }

  return hasMatchedIdentity
}
